import { DeviceIssueType } from '@/types/devices';
import {
  DeviceIssuesData,
  getDeviceIssueNameMap,
  DeviceIssueStatus,
} from '@/components/Assets/AssetDetails/DeviceSecurityPanel/DeviceSecurityPanel';
import {
  Flex,
  Spacer,
  Table,
  TableContainer,
  TabPanel,
  Text,
  spaces,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';
import { AssetDetailsTabs } from '@/pages/Assets/types';
import getSecurityPoliciesColumns from '@/components/Assets/AssetDetails/DeviceSecurityPanel/getSecurityPoliciesColumns';
import getPerformanceIssuesColumns from './getPerformanceIssuesColumns';
import { DeviceNotEnrolledBanner } from '@/pages/Assets/AssetDetailsContent/DeviceNotEnrolledBanner';
import { DeviceNotApplicableBanner } from '@/pages/Assets/AssetDetailsContent/DeviceNotApplicableBanner';
import { Asset } from '@/types/assets';
import { AssetsMdmStatus } from '@electricjs/core_entity-client';
import { useFlags } from 'launchdarkly-react-client-sdk';

export const DeviceHealthUnknownPanel = ({ asset }: { asset: Asset }) => {
  const { t } = useTranslation('assets');
  const deviceSecurityPoliciesColumns = getSecurityPoliciesColumns(t);
  const devicePerformanceIssuesColumns = getPerformanceIssuesColumns(t);
  const { space_m, space_l } = spaces;
  const deviceIssueNameMap = getDeviceIssueNameMap(t);
  const { ithEnableNotApplicableMdmFlagForDevices } = useFlags();

  const isNotApplicable =
    ithEnableNotApplicableMdmFlagForDevices &&
    asset.mdmStatusOverride === AssetsMdmStatus.NotApplicable;

  const unknownDeviceSecurityPoliciesIssuesData: DeviceIssuesData[] = [
    {
      name: deviceIssueNameMap[DeviceIssueType.Firewall],
      icon: 'firewall',
      hasData: false,
      hasIssue: false,
      disabled: false,
      status: DeviceIssueStatus.Unknown,
    },
    {
      name: deviceIssueNameMap[DeviceIssueType.Gatekeeper],
      icon: 'logout',
      hasData: false,
      hasIssue: false,
      disabled: false,
      status: DeviceIssueStatus.Unknown,
    },
    {
      name: deviceIssueNameMap[DeviceIssueType.Encryption],
      icon: 'key',
      hasData: false,
      hasIssue: false,
      disabled: false,
      status: DeviceIssueStatus.Unknown,
    },
    {
      name: deviceIssueNameMap[DeviceIssueType.OS],
      icon: 'cog',
      hasData: false,
      hasIssue: false,
      disabled: false,
      status: DeviceIssueStatus.Unknown,
    },
  ];

  const unknownDevicePerformanceIssuesData: DeviceIssuesData[] = [
    {
      name: deviceIssueNameMap[DeviceIssueType.RAM],
      icon: 'ram',
      hasData: false,
      hasIssue: false,
      disabled: false,
      status: DeviceIssueStatus.Unknown,
    },
    {
      name: deviceIssueNameMap[DeviceIssueType.Storage],
      icon: 'storage',
      hasData: false,
      hasIssue: false,
      disabled: false,
      status: DeviceIssueStatus.Unknown,
    },
  ];

  return (
    <TabPanel tabId={AssetDetailsTabs.Health}>
      <Spacer y={space_m} />
      {isNotApplicable ? (
        <DeviceNotApplicableBanner asset={asset} />
      ) : (
        <DeviceNotEnrolledBanner asset={asset} />
      )}
      <Spacer y={space_m} />
      <Flex gap={space_m}>
        <TableContainer flexDirection="column" flex="1" overflow="visible">
          <Text variant="heading-3" padding={space_l}>
            {t('deviceSecurityPanel.securityPolicies')}
          </Text>
          <Table
            id="unknown-device-security-policies-table"
            data={unknownDeviceSecurityPoliciesIssuesData}
            columns={deviceSecurityPoliciesColumns}
            loading={false}
          />
        </TableContainer>
        <TableContainer flexDirection="column" flex="1" overflow="visible">
          <Text variant="heading-3" padding={space_l}>
            {t('deviceSecurityPanel.performanceIssues')}
          </Text>
          <Table
            id="unknown-device-performance-issues-table"
            data={unknownDevicePerformanceIssuesData}
            columns={devicePerformanceIssuesColumns}
            loading={false}
          />
        </TableContainer>
      </Flex>
    </TabPanel>
  );
};

export default DeviceHealthUnknownPanel;
